
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ViewChild,
} from '@angular/core';
import {
  ObjectKey,
  PersistenceService,
  PersistMode,
  PersistObjectModel,
  TargetColumnValue,
  TargetObjectData,
} from '@barthauer/abc/core/data';
import { _AbcButtonComponent, _AbcGridComponent } from '@barthauer/abc/ui-components';
import { Collaborator, MeasureTask, Resource } from '@barthauer/custom/xmt-models';
import { DxButtonModule, DxDataGridModule } from 'devextreme-angular';
import { Subscription } from 'rxjs';
import { NotificationMessage, ToastType } from '../../../../../models/enumeration-list.model';
import { DataService, ObjectKeys } from '../../../../../services/data.service';
import { NotificationService } from '../../../../../services/notification.service';
import { SharedModule } from '../../../../../shared/shared.module';
import { MeasureService } from '../../../measure.service';
import { RowValidatingEvent } from 'devextreme/ui/data_grid';

@Component({
  selector: 'ubt-geods-collaborators',
  templateUrl: './collaborators.component.html',
  styleUrls: ['./collaborators.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DxDataGridModule, _AbcGridComponent, DxButtonModule, SharedModule, _AbcButtonComponent]
})
export class CollaboratorsComponent implements OnChanges {
  @ViewChild('collaboratorGrid') collaboratorGrid: _AbcGridComponent;
  @Input('dataSource') collaboratorsData: Collaborator[] = [];
  @Input('currentMeasureTask') measureTask: MeasureTask = new MeasureTask(null);
  @Input('assignedResources') assignedResources: Resource[] = [];
  @Input() totalTimePerCollaboratorPerMonth: Map<string, number>;
  @Output() onCalculateOverallTime: EventEmitter<number> = new EventEmitter();
  @Input() canManageTasks: boolean;
  public noDataText = 'Keine Daten';
  private subscriptions: Subscription[] = [];
  constructor(
    private cdr: ChangeDetectorRef,
    public dataService: DataService,
    public measureService: MeasureService,
    private persistenceService: PersistenceService,
    private notificationService: NotificationService
  ) { }

  public ngOnChanges(SimpleChanges): void {

    if (this.collaboratorsData === undefined) this.collaboratorsData = [];
    this.cdr.markForCheck();

  }

  public ngOnInit(): void { }

  onRowValidating(e: RowValidatingEvent): void {

    if (!e.newData?.IdRefMember) {
      return;
    }

    const newResourceId = e.newData.IdRefMember;
    const isNewRow = !e.oldData || !e.oldData.Id;


    const duplicateExists = this.collaboratorsData.some(row => {

      if (!isNewRow && row.Id === e.oldData.Id) {
        return false;
      }
      return row['IdRefMember'] === newResourceId;
    });

    if (duplicateExists) {
      e.errorText = this.dataService.res('Ubt-MeasureTask-Asign-Resource-to-Task')
      e.isValid = false;
    }
  }
  public cellPrepared = (e) => {

    if (e.column.dataField === 'TimeExecuted' && e.rowType === 'data') {
      if (e.row.data.TimeExecuted > e.row.data.TimePlanned) {
        e.cellElement.style.color = 'red';
      } else {
        e.cellElement.style.color = 'green';
      }
    }
  };

  public removeCollaborator(e) {
    this.subscriptions.push(
      this.dataService.deleteObject(ObjectKeys.COLLABORATOR, e.data.Id).subscribe(
        () => {
          this.notificationService.callNotification({
            message: NotificationMessage.ValidDeleteMessage,
            type: ToastType.SUCCESS,
          });
          this.cdr.markForCheck();
        },
        (error) => {
          console.error(error);
          this.notificationService.callNotification({ message: NotificationMessage.Error, type: ToastType.ERROR });
        }
      )
    );
  }

  public saveCollaborator(e) {
    if (e.changes[0].type !== 'remove' && this.measureService.isCreatingSubTask === false) {
      const newCollaborator = e.changes[0].data;
      const collaboratorPersistQuery: TargetObjectData = new TargetObjectData();
      collaboratorPersistQuery.ObjectKey = new ObjectKey();
      collaboratorPersistQuery.ObjectKey.ObjectType = ObjectKeys.COLLABORATOR;
      const collaboratorColumns: TargetColumnValue[] = [
        { Name: 'ParentId', Value: this.measureTask.Id },
        { Name: 'IdRefMeasureResource', Value: newCollaborator.IdRefMember },
        { Name: 'TimeExecuted', Value: newCollaborator.TimeExecuted },
        { Name: 'TimePlanned', Value: newCollaborator.TimePlanned },
        { Name: 'Period', Value: new Date().toDateString() },
      ];
      if (e.changes[0].type === 'update') {
        collaboratorPersistQuery.Mode = PersistMode.Update;
        collaboratorPersistQuery.ObjectKey.Id = newCollaborator.Id;
      } else {
        collaboratorPersistQuery.Mode = PersistMode.Insert;
      }
      collaboratorPersistQuery.TargetColumns = collaboratorColumns;
      const persistObject: PersistObjectModel = new PersistObjectModel();
      persistObject.Object = collaboratorPersistQuery;
      this.subscriptions.push(
        this.persistenceService.executePersistObjectQuery(persistObject).subscribe(
          (collaborator: any) => {
            this.cdr.markForCheck();
            if (!this.collaboratorsData[this.collaboratorsData.length - 1]?.Id) {
              this.collaboratorsData[this.collaboratorsData.length - 1].Id = collaborator.Id;
            }
            this.measureService.realTimecalculateTotalTime(true);
            this.notificationService.callNotification({
              message: NotificationMessage.ValidInsertMessage,
              type: ToastType.SUCCESS,
            });
          },
          (error) => {
            this.notificationService.callNotification({ message: NotificationMessage.Error, type: ToastType.ERROR });
          },
          () => { }
        )
      );
    } else if (e.changes[0].type !== 'remove' && this.measureService.isCreatingSubTask === true) {
      this.measureTask.Collaborators = this.collaboratorsData;
    }
  }

  public addCollaborator(): void {
    this.collaboratorGrid.instance.addRow();
  }

  public calculateOverallTime(): void {
    const sumWithInitial = this.collaboratorsData.reduce(
      (previousValue, currentValue) => previousValue + parseInt(currentValue.TimePlanned.toString()),
      0
    );
    this.onCalculateOverallTime.emit(sumWithInitial);
  }

  public ngOnDestroy(): void {
    this.subscriptions.map((subscription) => subscription.unsubscribe());
  }
}

export const VISIBLE_PROPERTIES = ['name', 'timePlanned', 'timeExecuted'];
